import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/session/useSession';

export function RequireAuth() {
  const { session, loading } = useSession();
  const location = useLocation();

  if (loading) return <FullScreenSpinner />;
  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <Outlet />;
}

// Only honour `next` when it is a same-site path (starts with a single `/`,
// not `//` or a scheme) to avoid an open redirect. The second character must
// reject `\` as well as `/`: browsers normalise backslashes to slashes in
// URLs, so `/\evil.com` would otherwise survive as the protocol-relative
// `//evil.com`.
function sanitizeNext(next: string | null): string {
  if (next && /^\/(?![/\\])/.test(next)) return next;
  return '/';
}

export function RedirectIfAuthed() {
  const { session, loading } = useSession();
  const [searchParams] = useSearchParams();

  if (loading) return <FullScreenSpinner />;
  if (session) {
    return <Navigate to={sanitizeNext(searchParams.get('next'))} replace />;
  }
  return <Outlet />;
}

export function FullScreenSpinner() {
  const { t } = useTranslation();
  return (
    <div className="grid min-h-screen place-items-center bg-bg">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent border-t-transparent" aria-label={t('loading')} />
    </div>
  );
}
